import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import RequestReceipt from "./RequestReceipt";
import { business } from "../siteData";

const common = [
  { name: "name", label: "Full name", type: "text", autoComplete: "name", required: true },
  { name: "phone", label: "Phone", type: "tel", autoComplete: "tel", required: true },
  { name: "email", label: "Email", type: "email", autoComplete: "email", required: true },
];

const fields = {
  vehicle: [
    { name: "vehicleType", label: "Vehicle or body style", type: "text", placeholder: "Example: 2022 to 2024 midsize SUV", required: true },
    { name: "budget", label: "Budget", type: "text", placeholder: "Purchase budget or comfortable range", required: true },
    { name: "requirements", label: "Must-haves and preferences", type: "textarea", placeholder: "Color, mileage, features, timing, and anything you will not compromise on", required: true },
    { name: "tradeIn", label: "Will you have a trade-in?", type: "select", options: ["No", "Yes", "Not sure yet"], required: true },
  ],
  // Date and time are not fields here. They come from AppointmentPicker, which
  // knows the opening hours and writes preferredDate and preferredTime itself.
  service: [
    { name: "vehicle", label: "Vehicle year, make, and model", type: "text", required: true },
    { name: "service", label: "Service needed", type: "select", options: ["Maintenance", "Brakes", "Diagnostics", "Steering or suspension", "Cooling or climate", "Tire service", "Something else"], required: true },
    { name: "notes", label: "What is the vehicle doing?", type: "textarea", placeholder: "Include symptoms, warning lights, recent work, or other useful context" },
  ],
  tire: [
    { name: "vehicle", label: "Vehicle year, make, and model", type: "text", required: true },
    { name: "tireSize", label: "Tire size, if known", type: "text", placeholder: "Example: 225/65R17" },
    { name: "drivingType", label: "Typical driving", type: "select", options: ["Daily commute", "Highway", "Truck and towing", "Performance"], required: true },
    { name: "notes", label: "What matters most?", type: "textarea", placeholder: "Ride, road noise, mileage, weather, budget, or other priorities" },
  ],
  appraisal: [
    { name: "vehicle", label: "Year, make, and model", type: "text", required: true },
    { name: "trim", label: "Trim", type: "text" },
    { name: "mileage", label: "Current mileage", type: "number", min: "0", required: true },
    { name: "condition", label: "Overall condition", type: "select", options: ["Excellent", "Good", "Fair", "Needs work", "Not sure"], required: true },
    { name: "vin", label: "VIN, if handy", type: "text" },
    { name: "photos", label: "Link to photos, if you have them", type: "url", placeholder: "Optional. A shared album or drive link is fine." },
    { name: "notes", label: "Anything we should know", type: "textarea", placeholder: "Known damage, warning lights, recent work, or other useful details" },
  ],
  general: [
    { name: "topic", label: "I am interested in", type: "select", options: ["Finding a vehicle", "Service", "Tires", "Selling or trading my vehicle", "Other"], required: true },
    { name: "message", label: "Message", type: "textarea", required: true },
  ],
};

function Field({ field }) {
  const props = {
    id: field.name,
    name: field.name,
    required: field.required,
    autoComplete: field.autoComplete,
    placeholder: field.placeholder,
    min: field.min,
  };

  return (
    <div className={field.type === "textarea" ? "form-field form-field-wide" : "form-field"}>
      <label htmlFor={field.name}>{field.label}{field.required ? <span aria-hidden="true"> *</span> : null}</label>
      {field.type === "textarea" ? (
        <textarea {...props} rows="5" />
      ) : field.type === "select" ? (
        <select {...props} defaultValue="">
          <option value="" disabled>Select one</option>
          {field.options.map((option) => <option key={option} value={option}>{option}</option>)}
        </select>
      ) : (
        <input {...props} type={field.type} />
      )}
    </div>
  );
}

const defaultIntro = "Share the useful details now. We will confirm the next step before anything is scheduled or committed.";

export default function InquiryForm({
  type,
  title,
  intro = defaultIntro,
  submitLabel = "Send request",
  successMessage = "Your request was received. Prime Path Motors will follow up using the contact details you provided.",
  // The request pages carry their own page heading above the cream band, so the
  // panel drops its intro column there and runs the fields full width.
  showIntro = true,
  // Rendered above the fields. Used by the booking page for the calendar.
  lead = null,
  // Returns an error string when the lead content is incomplete, or null.
  // Custom controls are buttons, so the browser cannot validate them for us.
  validateLead = null,
  // form.reset() clears native inputs only. Anything the lead holds in React
  // state has to be cleared by whoever owns that state.
  onSuccess = null,
  // Where Cancel goes. A form with no way out but the browser button is the
  // complaint this answers.
  cancelTo = null,
  cancelLabel = "Cancel",
}) {
  const [state, setState] = useState({ status: "idle", message: "" });
  const [leadError, setLeadError] = useState(null);
  // Held so the confirmation screen can read the submission back to the customer.
  const [receipt, setReceipt] = useState(null);
  // Same-origin by default, so production needs no build-time configuration and
  // no destination address is ever shipped to the browser. The env var stays as
  // an override for preview builds pointed at a different host.
  const endpoint = import.meta.env.VITE_FORM_ENDPOINT || "/api/inquiry";
  const formFields = useMemo(() => [...common, ...fields[type]], [type]);

  async function submit(event) {
    event.preventDefault();
    // event.currentTarget is only valid while the event is dispatching. Read it
    // once, here, because after the first await it is null and touching it
    // throws, which the catch below would report to the customer as a failed
    // submission on a request that actually succeeded.
    const form = event.currentTarget;

    const missing = validateLead ? validateLead() : null;
    setLeadError(missing);
    if (missing) {
      form.querySelector(".appt-picker")?.scrollIntoView({ block: "center", behavior: "smooth" });
      return;
    }

    setState({ status: "submitting", message: "" });
    if (!endpoint) {
      setState({ status: "error", message: "Online submission is not connected yet. Please call or email us and we will take it from there." });
      return;
    }

    try {
      const values = Object.fromEntries(new FormData(form).entries());
      const response = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ inquiryType: type, ...values }),
      });
      if (!response.ok) throw new Error(`Request failed with status ${response.status}`);
      // The server mints the reference and puts the same one in both emails, so
      // the number the customer reads out on the phone matches Jerome's inbox.
      const body = await response.json().catch(() => ({}));
      form.reset();
      onSuccess?.();
      setReceipt({ reference: body.reference || "", values });
      setState({ status: "success", message: successMessage });
    } catch {
      setState({ status: "error", message: "We could not send this request. Please call or email us instead." });
    }
  }

  // The confirmation replaces the form. Leaving a filled-in form on screen under
  // a success line is what made a delivered request read as uncertain, and it
  // invites a second submission of the same request.
  if (receipt) {
    return (
      <section className={`form-panel${showIntro ? "" : " form-panel-wide"}`}>
        <RequestReceipt
          type={type}
          reference={receipt.reference}
          values={receipt.values}
          onReset={() => { setReceipt(null); setState({ status: "idle", message: "" }); }}
        />
      </section>
    );
  }

  return (
    <section className={`form-panel${showIntro ? "" : " form-panel-wide"}`} aria-labelledby={`${type}-form-title`}>
      {showIntro ? (
        <div className="form-intro">
          <p className="eyebrow">Start here</p>
          <h2 id={`${type}-form-title`}>{title}</h2>
          <p>{intro}</p>
        </div>
      ) : (
        <h2 id={`${type}-form-title`} className="visually-hidden">{title}</h2>
      )}
      <form className="inquiry-form" onSubmit={submit} noValidate={false}>
        {lead ? React.cloneElement(lead, { error: leadError }) : null}
        <div className="form-grid">{formFields.map((field) => <Field key={field.name} field={field} />)}</div>
        {/* Honeypot. Hidden from people and from screen readers, irresistible to
            bots. The server discards any submission that fills it. */}
        <div className="honeypot" aria-hidden="true">
          <label htmlFor="company-website">Company</label>
          <input id="company-website" name="company" type="text" tabIndex={-1} autoComplete="off" />
        </div>
        <label className="consent-field">
          <input name="contactConsent" type="checkbox" required />
          <span>I agree that Prime Path Motors may contact me about this request. *</span>
        </label>
        {/* Cancel sits beside Submit, the way Jobber does it, and it names where
            it goes rather than leaving the visitor to guess. */}
        <div className="form-actions">
          <button className="button button-gold" type="submit" disabled={state.status === "submitting"}>
            {state.status === "submitting" ? "Sending" : submitLabel}
          </button>
          {cancelTo ? <Link className="button button-quiet" to={cancelTo}>{cancelLabel}</Link> : null}
        </div>
        <p className="form-note">Prefer to talk? <a href={`tel:${business.phoneHref}`}>{business.phoneDisplay}</a> · <a href={`mailto:${business.email}`}>{business.email}</a></p>
        {state.message ? <div className={`form-status ${state.status}`} role={state.status === "error" ? "alert" : "status"}>{state.message}</div> : null}
      </form>
    </section>
  );
}
